import type { HallManifest } from '@mawkeb/shared/manifest';
import type { MeasuredModel, Vec3 } from '@mawkeb/shared/model';
import { FT, IN, type UnitSystem } from '@mawkeb/shared/units';
import { create } from 'zustand';
import type { DeviceInfo } from '../engine/quality';

export type ViewMode = 'plan' | 'orbit' | 'walk';
/** What the hall is drawn from: the splat scan, or the measured shape covered with the hall's photos. */
export type Look = 'scan' | 'photos';
export type ThemePref = 'system' | 'light' | 'dark';
export type Theme = 'light' | 'dark';
export type Tool = 'none' | 'tape';

export const DEFAULT_EYE_HEIGHT = 5 * FT + 4 * IN;
export const EYE_HEIGHT_RANGE: [number, number] = [3 * FT, 7 * FT];

export interface Measurement {
  id: string;
  a: Vec3;
  b: Vec3;
  /** What each end snapped to ("Back wall", "Stage front", "a point on the floor"). */
  snapA: string;
  snapB: string;
}

export type LoadPhase = 'starting' | 'manifest' | 'plan' | 'coarse' | 'streaming' | 'ready' | 'error';

export interface LoadState {
  phase: LoadPhase;
  message?: string;
  /** Bytes of the scan on hand (downloaded or from the device) out of the total. */
  bytes: number;
  totalBytes: number;
  fromDevice: number;
  /** ms since the page started, for each step of the load order. */
  timings: Partial<Record<'shell' | 'manifest' | 'plan' | 'engine' | 'coarse' | 'full', number>>;
}

export interface QualityState {
  tier: string;
  gpu: string;
  pixelRatio: number;
  splatBudget: number;
  splatsShown: number;
  frameMs: number;
  fps: number;
  stepDowns: number;
}

export interface Toast {
  id: number;
  text: string;
  tone?: 'bad';
  action?: { label: string; run: () => void };
}

interface Prefs {
  themePref: ThemePref;
  units: UnitSystem;
  eyeHeight: number;
}

export interface VenueState extends Prefs {
  theme: Theme;
  view: ViewMode;
  /** Only matters for a hall published with a photo hall beside its scan. */
  look: Look;
  clean: boolean;
  tool: Tool;
  menuOpen: boolean;
  /** The prop library is open (a panel on a laptop, a sheet on a phone). */
  library: boolean;
  /** Whether the layout in the hall can be changed (not through a view-only link, nor an old version). */
  editable: boolean;
  /** The library's pictures, rendered by the app once the 3D engine is up: item id to image URL. */
  thumbs: Record<string, string>;
  diagnostics: boolean;
  online: boolean;
  updateReady: boolean;
  contextLost: boolean;
  /** The 3D engine has drawn its first frame (the plan preview steps aside). */
  engineDrawing: boolean;
  /** What the browser says about the device, read once at start. */
  device: DeviceInfo | null;

  manifest: HallManifest | null;
  model: MeasuredModel | null;
  load: LoadState;
  quality: QualityState;
  measurements: Measurement[];
  pendingTape: { a: Vec3; snap: string } | null;
  /** Holds tape lines square to the hall (Shift does the same on a keyboard). */
  tapeStraight: boolean;
  /** Screen space the panels cover, so the plan view fits the hall in what is left. */
  insets: { top: number; bottom: number; left: number; right: number };
  toasts: Toast[];

  set: (patch: Partial<VenueState>) => void;
  setPref: <K extends keyof Prefs>(key: K, value: Prefs[K]) => void;
  setLoad: (patch: Partial<LoadState>) => void;
  mark: (step: keyof LoadState['timings']) => void;
  toast: (text: string, opts?: { tone?: 'bad'; action?: Toast['action']; ms?: number }) => void;
  dismiss: (id: number) => void;
}

const STORAGE = { themePref: 'venue.theme', units: 'venue.units', eyeHeight: 'venue.eyeHeight' } as const;

function readPrefs(): Prefs {
  const get = (k: string) => {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  };
  const theme = get(STORAGE.themePref);
  const units = get(STORAGE.units);
  const eye = Number(get(STORAGE.eyeHeight));
  return {
    themePref: theme === 'light' || theme === 'dark' ? theme : 'system',
    units: units === 'metric' ? 'metric' : 'imperial',
    eyeHeight: eye >= EYE_HEIGHT_RANGE[0] && eye <= EYE_HEIGHT_RANGE[1] ? eye : DEFAULT_EYE_HEIGHT,
  };
}

function systemTheme(): Theme {
  return typeof matchMedia !== 'undefined' && matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function resolveTheme(pref: ThemePref): Theme {
  return pref === 'system' ? systemTheme() : pref;
}

let toastId = 0;

const prefs = readPrefs();

function readView(): ViewMode {
  try {
    const v = new URLSearchParams(location.search).get('view') ?? localStorage.getItem('venue.view');
    return v === 'plan' || v === 'walk' || v === 'orbit' ? v : 'orbit';
  } catch {
    return 'orbit';
  }
}

export function rememberView(view: ViewMode) {
  try {
    localStorage.setItem('venue.view', view);
  } catch {
    /* private mode */
  }
}

function readLook(): Look {
  try {
    const v = new URLSearchParams(location.search).get('look') ?? localStorage.getItem('venue.look');
    return v === 'photos' ? 'photos' : 'scan';
  } catch {
    return 'scan';
  }
}

export function rememberLook(look: Look) {
  try {
    localStorage.setItem('venue.look', look);
  } catch {
    /* private mode */
  }
}

export const useVenue = create<VenueState>()((set, get) => ({
  ...prefs,
  theme: resolveTheme(prefs.themePref),
  view: readView(),
  look: readLook(),
  clean: false,
  tool: 'none',
  menuOpen: false,
  library: false,
  editable: true,
  thumbs: {},
  diagnostics: new URLSearchParams(location.search).has('diag'),
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  updateReady: false,
  contextLost: false,
  engineDrawing: false,
  device: null,

  manifest: null,
  model: null,
  load: { phase: 'starting', bytes: 0, totalBytes: 0, fromDevice: 0, timings: {} },
  quality: { tier: '', gpu: '', pixelRatio: 1, splatBudget: 0, splatsShown: 0, frameMs: 0, fps: 0, stepDowns: 0 },
  measurements: [],
  pendingTape: null,
  tapeStraight: false,
  insets: { top: 76, bottom: 64, left: 12, right: 12 },
  toasts: [],

  set: (patch) => set(patch),
  setPref: (key, value) => {
    try {
      localStorage.setItem(STORAGE[key], String(value));
    } catch {
      /* private mode: the choice lasts for this visit */
    }
    const patch: Partial<VenueState> = { [key]: value };
    if (key === 'themePref') patch.theme = resolveTheme(value as ThemePref);
    set(patch);
  },
  setLoad: (patch) => set({ load: { ...get().load, ...patch } }),
  mark: (step) => {
    const t = Math.round(performance.now());
    performance.mark(`venue:${step}`);
    const load = get().load;
    if (load.timings[step] === undefined) set({ load: { ...load, timings: { ...load.timings, [step]: t } } });
  },
  toast: (text, opts = {}) => {
    const id = ++toastId;
    set({ toasts: [...get().toasts, { id, text, tone: opts.tone, action: opts.action }] });
    const ms = opts.ms ?? (opts.action ? 8000 : 3500);
    if (ms > 0) setTimeout(() => get().dismiss(id), ms);
  },
  dismiss: (id) => set({ toasts: get().toasts.filter((t) => t.id !== id) }),
}));

export const venue = useVenue.getState;
